
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface EnrollDeviceDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EnrollDeviceDialog = ({ isOpen, onClose }: EnrollDeviceDialogProps) => {
  const [deviceName, setDeviceName] = useState("");
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [enrollmentUrl, setEnrollmentUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleEnroll = async () => {
    try {
      setIsLoading(true);
      
      console.log('Starting device enrollment process...');
      
      const { data, error } = await supabase.functions.invoke('enroll-device', {
        body: {
          name: deviceName,
          requires_auth: requiresAuth,
          enrollment_username: requiresAuth ? username : null,
          enrollment_password: requiresAuth ? password : null
        }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      console.log('Enrollment data received:', data);

      // Ensure we're using HTTPS for the enrollment URL
      const enrollUrl = `https://twyvfimczlphkoweqcbh.supabase.co/functions/v1/verify-enrollment?device_id=${data.device_id}&token=${data.enrollment_token}`;
      console.log('Generated enrollment URL:', enrollUrl);
      
      setEnrollmentUrl(enrollUrl);
      toast.success('Device enrollment initiated successfully');
    } catch (error) {
      console.error('Error enrolling device:', error);
      toast.error('Failed to initiate device enrollment');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!enrollmentUrl) return;
    
    try {
      await navigator.clipboard.writeText(enrollmentUrl);
      console.log('Enrollment URL copied:', enrollmentUrl);
      toast.success('Enrollment URL copied to clipboard');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy enrollment URL');
    }
  };

  const resetForm = () => {
    setDeviceName("");
    setRequiresAuth(false);
    setUsername("");
    setPassword("");
    setEnrollmentUrl(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Enroll New Device</DialogTitle>
          <DialogDescription>
            Enter the device details to generate an enrollment URL.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Device Name</Label>
            <Input
              id="name"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="Enter device name"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="requires-auth"
              checked={requiresAuth}
              onCheckedChange={setRequiresAuth}
            />
            <Label htmlFor="requires-auth">Require Authentication</Label>
          </div>

          {requiresAuth && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                />
              </div>
            </>
          )}

          {enrollmentUrl ? (
            <div className="space-y-4">
              <div className="p-2 bg-muted rounded-md break-all">
                <p className="text-sm">{enrollmentUrl}</p>
              </div>
              <Button onClick={copyToClipboard} className="w-full">
                Copy Enrollment URL
              </Button>
            </div>
          ) : (
            <Button 
              onClick={handleEnroll} 
              disabled={!deviceName || (requiresAuth && (!username || !password)) || isLoading}
            >
              Generate Enrollment URL
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
